/**
 * Harmony Snackbar Container Component
 *
 * Container component that manages snackbar queue/stack and CDK Overlay attachment. Handles multiple snackbar display
 * strategies (sequential or stack).
 *
 * @internal - Not directly used by consumers
 */
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  inject,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { HxSnackbar } from './snackbar';
import { HxSnackbarConfig } from './snackbar-config';
import { getAriaPoliteness, getSnackbarIcon, HxSnackbarData } from './snackbar-data';
import { HxSnackbarRef } from './snackbar-ref';

type SnackbarQueueItem = {
  ref: HxSnackbarRef;
  config: HxSnackbarConfig;
  componentRef?: ComponentRef<HxSnackbar>;
  /** Marks items that are exiting due to stack overflow */
  isPendingRemoval?: boolean;
};

@Component({
  selector: 'hx-snackbar-container',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './snackbar-container.html',
  styleUrls: ['./snackbar-container.css'],
  host: {
    '[class]': "'hx-snackbar-container ' + position",
    '[attr.aria-live]': '"polite"',
    '[attr.aria-relevant]': '"additions"',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxSnackbarContainer implements OnDestroy {
  /** View container for dynamic component creation */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @ViewChild('snackbarOutlet', { read: ViewContainerRef, static: true })
  snackbarOutlet!: ViewContainerRef;

  /** CDK LiveAnnouncer for screen reader support */
  private liveAnnouncer = inject(LiveAnnouncer);

  /** Display strategy */
  displayStrategy: 'sequential' | 'stack' = 'stack';

  /** Position for container (top-right, top-left, etc.) */
  position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center' = 'bottom-right';

  /** Maximum snackbars in stack mode */
  maxStack = 3;

  /** Queue for sequential mode */
  private queue: SnackbarQueueItem[] = [];

  /** Active snackbars (stack mode or current sequential item) */
  private activeSnackbars: SnackbarQueueItem[] = [];

  /** Count only currently visible (non-exiting) snackbars */
  private getVisibleStackCount(): number {
    return this.activeSnackbars.filter((item) => !item.isPendingRemoval).length;
  }

  /** Attach a snackbar to the container */
  attach(config: HxSnackbarConfig, ref: HxSnackbarRef): void {
    const fallbackMessage = config.message && config.message.trim().length > 0 ? config.message : 'Notification';
    const announcement =
      config.announcementMessage && config.announcementMessage.trim().length > 0
        ? config.announcementMessage
        : fallbackMessage;

    // Create internal data structure
    const data: HxSnackbarData = {
      message: fallbackMessage,
      variant: config.variant || 'fill',
      context: config.context || 'brand',
      hasIcon: config.hasIcon || false,
      icon: getSnackbarIcon(config.context || 'brand', config.hasIcon || false),
      hasAction: config.hasAction || false,
      actionLabel: config.actionLabel,
      onAction: config.onAction,
      hasClose: config.hasClose || false,
      snackbarRef: ref,
      customClass: config.customClass,
      ariaLive: getAriaPoliteness(config.context || 'brand', config.ariaLive),
      announcementMessage: announcement,
    };

    // Set display strategy and position from config
    this.displayStrategy = config.displayStrategy || 'stack';
    this.position = config.position || 'bottom-right';
    this.maxStack = typeof config.maxVisible === 'number' && config.maxVisible > 0 ? config.maxVisible : 10;

    // Create component
    const componentRef = this.snackbarOutlet.createComponent(HxSnackbar);
    if (this.isTopPosition()) {
      // Move newest snackbar closest to the top anchor when using top-* positions
      this.snackbarOutlet.move(componentRef.hostView, 0);
    }
    componentRef.instance.data = data;
    ref.instance = componentRef.instance;

    const queueItem: SnackbarQueueItem = {
      ref,
      config,
      componentRef,
      isPendingRemoval: false,
    };

    // Handle based on strategy
    if (this.displayStrategy === 'sequential') {
      this.handleSequential(queueItem, data);
    } else {
      this.handleStack(queueItem, data);
    }

    // Wire enter animation → ref.afterOpened() + start auto-dismiss after snackbar is fully visible
    componentRef.instance._onEnter.subscribe(() => {
      ref._open();
      if (config.duration && config.duration > 0) {
        ref._startAutoDismissTimer(config.duration);
      }
    });

    // Announce to screen readers after DOM has settled (150ms matches Angular Material)
    setTimeout(() => {
      void this.liveAnnouncer.announce(data.announcementMessage, data.ariaLive);
    }, 150);
  }

  /** Handle sequential display strategy */
  private handleSequential(item: SnackbarQueueItem, _data: HxSnackbarData): void {
    if (this.activeSnackbars.length === 0) {
      // Show immediately
      this.activeSnackbars.push(item);
    } else {
      // Add to queue
      this.queue.push(item);
      // Hide component until ready
      if (item.componentRef) {
        item.componentRef.location.nativeElement.style.display = 'none';
      }
    }
  }

  /** Handle stack display strategy */
  private handleStack(item: SnackbarQueueItem, _data: HxSnackbarData): void {
    // Enforce max stack limit without prematurely removing view references
    while (this.getVisibleStackCount() >= this.maxStack) {
      const oldest = this.activeSnackbars.find((entry) => !entry.isPendingRemoval);
      if (!oldest) {
        break;
      }
      oldest.isPendingRemoval = true;
      oldest.ref.dismiss();
    }

    // Add to stack
    this.activeSnackbars.push(item);

    // Reset and update positions for all snackbars
    setTimeout(() => {
      this.updateStackPositions();
    }, 0);
  }

  /** Called when a snackbar is dismissed */
  onSnackbarDismissed(ref: HxSnackbarRef): void {
    // Find and remove from active snackbars
    const index = this.activeSnackbars.findIndex((item) => item.ref === ref);
    if (index !== -1) {
      const item = this.activeSnackbars[index];
      if (item) {
        this.activeSnackbars.splice(index, 1);

        // Destroy component
        if (item.componentRef) {
          item.componentRef.destroy();
        }

        // Process next in queue (sequential mode) or update positions (stack mode)
        if (this.displayStrategy === 'sequential') {
          this.processQueue();
        } else {
          // Update stack positions after a brief delay to allow destroy to complete
          setTimeout(() => {
            this.updateStackPositions();
          }, 10);
        }
      }
    }
  }

  /** Process next item in queue */
  private processQueue(): void {
    if (this.queue.length > 0 && this.activeSnackbars.length === 0) {
      const next = this.queue.shift();
      if (next) {
        this.activeSnackbars.push(next);
        // Show component
        if (next.componentRef) {
          next.componentRef.location.nativeElement.style.display = '';
        }
      }
    }
  }

  /** Update positions of stacked snackbars */
  private updateStackPositions(): void {
    const gap = 8; // 8px gap between snackbars
    const visibleSnackbars = this.activeSnackbars.filter((item) => !item.isPendingRemoval);
    const orderedSnackbars = this.isTopPosition() ? [...visibleSnackbars].reverse() : visibleSnackbars;

    // Reset all positions first
    orderedSnackbars.forEach((item, index) => {
      if (item.componentRef) {
        const element = item.componentRef.location.nativeElement as HTMLElement;

        // Ensure element is visible and properly styled
        element.style.display = '';
        element.style.opacity = '1';

        // Add transition for smooth animation
        element.style.transition = 'margin-top 300ms cubic-bezier(0.25, 0.8, 0.25, 1), opacity 150ms ease';

        // Set margin for gap (first item has no top margin, others have gap)
        element.style.marginTop = index === 0 ? '0px' : `${gap}px`;
        element.style.marginBottom = '0px';
        element.style.marginLeft = '0px';
        element.style.marginRight = '0px';
      }
    });
  }

  private isTopPosition(): boolean {
    return this.position.startsWith('top');
  }

  /** Dismiss all active snackbars cleanly, allowing afterDismissed() observables to complete */
  dismissAll(): void {
    const toDissmiss = [...this.activeSnackbars, ...this.queue];
    toDissmiss.forEach((item) => item.ref.dismiss());
    this.queue = [];
  }

  /** Cleanup on destroy */
  ngOnDestroy(): void {
    // Dismiss all active snackbars
    this.activeSnackbars.forEach((item: SnackbarQueueItem) => {
      if (item && item.componentRef) {
        item.componentRef.destroy();
      }
    });

    // Clear queue
    this.queue.forEach((item: SnackbarQueueItem) => {
      if (item && item.componentRef) {
        item.componentRef.destroy();
      }
    });

    this.activeSnackbars = [];
    this.queue = [];
  }
}
